import React, { useState, useEffect, useCallback, useMemo } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import { PlusCircle, FilterIcon, ArrowLeft, Building2, Search, X } from 'lucide-react';
import { useSearchParams, Link } from 'react-router-dom';
import AppliedFilters from '@/components/AppliedFilters';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import DynamicFormModal from '@/components/DynamicFormModal';
import { useToast } from '@/components/ui/use-toast';
import { usersColumns, columnsExcel } from '@/pages/parametrizacion/usuarios/utils/usersColumns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fetchUsers, createUser, updateUser, deleteUser, exportUsers } from '@/pages/parametrizacion/usuarios/Services/users.services';
import AsignarClientesConductorModal from '@/pages/parametrizacion/usuarios/AsignarClientesConductorModal';
import AsignarClientePrendasModal from '@/pages/parametrizacion/usuarios/AsignarClientePrendasModal';
import CambiarContrasenaModal from '@/pages/parametrizacion/usuarios/CambiarContrasenaModal';
import AsignarModulosUsuarioModal from '@/pages/parametrizacion/usuarios/AsignarModulosUsuarioModal';
import AsignarCentrosUsuarioModal from '@/pages/parametrizacion/usuarios/AsignarCentrosUsuarioModal';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';

const ITEMS_PER_PAGE = 10;
const DEFAULT_FILTERS = { estado: 'Activo' };

const UsuariosPage = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const idLavanderia = searchParams.get('lavanderia');
  const [laundryName, setLaundryName] = useState('');

  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState(
    idLavanderia ? { ...DEFAULT_FILTERS, 'lavanderia.id': idLavanderia } : DEFAULT_FILTERS
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [modalMode, setModalMode] = useState('create');

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [laundryOptions, setLaundryOptions] = useState([]);

  const [isAsignarClientesOpen, setIsAsignarClientesOpen] = useState(false);
  const [conductorSeleccionado, setConductorSeleccionado] = useState(null);

  const [isAsignarModulosOpen, setIsAsignarModulosOpen] = useState(false);
  const [usuarioParaModulos, setUsuarioParaModulos] = useState(null);

  const [isAsignarCentrosOpen, setIsAsignarCentrosOpen] = useState(false);
  const [usuarioParaCentros, setUsuarioParaCentros] = useState(null);

  const [isAsignarClientePrendasOpen, setIsAsignarClientePrendasOpen] = useState(false);
  const [clientePrendasUsuario, setClientePrendasUsuario] = useState(null);

  const [isCambiarContrasenaOpen, setIsCambiarContrasenaOpen] = useState(false);
  const [usuarioParaContrasena, setUsuarioParaContrasena] = useState(null);

  // ─────────────────────────────────────────────────────────────
  // Búsqueda rápida (client-side) — métodos propios, no tocan
  // los existentes para no afectar el resto de la app.
  // Busca sobre todos los usuarios activos (no solo la página
  // actual) para que sea útil cuando el usuario está en página 5
  // y quiere encontrar uno de la página 1.
  // ─────────────────────────────────────────────────────────────
  const [quickSearch, setQuickSearch] = useState('');
  const [allUsersForSearch, setAllUsersForSearch] = useState([]);

  // Carga el universo de búsqueda rápida. Reutiliza `fetchUsers` pero
  // NO comparte estado con el fetch paginado de la tabla: cada uno
  // mantiene su propio `query` y su propio resultado.
  const loadAllUsersForSearch = useCallback(async () => {
    try {
      const { data } = await fetchUsers({
        limit: 1000,
        offset: 1,
        query: { estado: 'Activo' },
      });
      setAllUsersForSearch(data || []);
    } catch (e) {
      // Silencioso: la búsqueda rápida es una mejora, no debe romper la página
      console.warn('No se pudo cargar el universo de búsqueda rápida', e);
    }
  }, []);

  // Recarga cuando cambia el total de usuarios (create/update/delete disparan
  // un re-fetch paginado que actualiza `totalItems`). No tocamos los handlers.
  useEffect(() => { loadAllUsersForSearch(); }, [totalItems, loadAllUsersForSearch]);

  // Filtro client-side: case-insensitive sobre los campos visibles en la tabla.
  const matchesUserSearch = (user, term) => {
    if (!term) return true;
    const t = term.toLowerCase().trim();
    if (!t) return true;
    return (
      user?.nombre?.toLowerCase().includes(t) ||
      user?.correo?.toLowerCase().includes(t) ||
      user?.cedula?.toLowerCase().includes(t) ||
      user?.rol?.toLowerCase().includes(t) ||
      user?.estado?.toLowerCase().includes(t) ||
      user?.lavanderia?.nombre?.toLowerCase().includes(t)
    );
  };

  // Lista que se muestra en la tabla:
  //  - sin búsqueda: usa la página actual del fetch paginado
  //  - con búsqueda: muestra todos los matches sobre el universo
  const dataToShow = useMemo(() => {
    const term = quickSearch.trim();
    if (!term) return displayedData;
    return allUsersForSearch.filter((u) => matchesUserSearch(u, term));
  }, [quickSearch, displayedData, allUsersForSearch]);

  const handleQuickSearchChange = (e) => setQuickSearch(e.target.value);
  const handleClearQuickSearch = () => setQuickSearch('');

  // Opciones de filtros: normalizamos a { value: id, label }
  const laundryOptionsFilter = useMemo(() => (
    (laundryOptions || []).map(opt => ({ value: opt?.value?.id, label: opt?.label }))
  ), [laundryOptions]);

  // Prefetch opciones de lavanderías al montar la página
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { fetchLaundries } = await import('@/pages/parametrizacion/laundries/Services/laundries.services');
        const r = await fetchLaundries({ limit: 200, offset: 1, query: { estado: 'Activo' } });
        const opts = (r.data || []).map(l => ({ value: { id: l._id, nombre: l.nombre }, label: l.nombre }));
        if (!cancelled) setLaundryOptions(opts);
      } catch (e) {
        console.error('Error precargando lavanderías', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchAndSetUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const offset = currentPage; // 1-based page
      const { data, totalRecords } = await fetchUsers({ limit: ITEMS_PER_PAGE, offset, query: filters });
      setDisplayedData(data);
      setTotalItems(totalRecords);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'No se pudieron obtener los usuarios.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, filters, toast]);

  useEffect(() => {
    fetchAndSetUsers();
  }, [fetchAndSetUsers]);

  // Fetch laundry name if coming from CentroParametrizacionPage
  useEffect(() => {
    if (idLavanderia) {
      const fetchLaundryName = async () => {
        try {
          const { fetchLaundryById } = await import('@/pages/parametrizacion/laundries/Services/laundries.services');
          const laundry = await fetchLaundryById(idLavanderia);
          setLaundryName(laundry?.nombre || '');
        } catch (e) {
          console.error('Error fetching laundry:', e);
        }
      };
      fetchLaundryName();
    }
  }, [idLavanderia]);

  const handlePageChange = (page) => setCurrentPage(page);

  const handleOpenModal = (mode, item = null) => {
    setModalMode(mode);
    setCurrentItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentItem(null);
  };

  const handleSaveItem = async (itemData) => {
    try {
      if (modalMode === 'create') {
        // Validación de confirmación de contraseña en el cliente
        if (itemData.contrasena !== itemData.contrasena2) {
          toast({ title: 'Error', description: 'Las contraseñas no coinciden', variant: 'destructive' });
          return;
        }
        // Remover campo auxiliar antes de enviar al backend
        const { contrasena2, ...payload } = itemData;
        const response = await createUser(payload);
        if (response.code === 201) {
          toast({ title: 'Éxito', description: 'Usuario creado correctamente.' });
        } else {
          toast({ title: 'Error', description: response.message || 'Error al crear usuario', variant: 'destructive' });
        }
      } else if (modalMode === 'edit') {
        await updateUser(currentItem._id, itemData);
        toast({ title: 'Éxito', description: 'Usuario actualizado correctamente.' });
      }
      handleCloseModal();
      fetchAndSetUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Ocurrió un problema al guardar el usuario.';
      toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
    }
  };

  const handleDeleteConfirmation = (item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteItem = async () => {
    try {
      if (itemToDelete) {
        const response = await deleteUser(itemToDelete._id);
        if (response.code === 200) {
          toast({ title: 'Usuario eliminado', description: 'El usuario fue eliminado correctamente.' });
        } else {
          throw new Error(response.message || 'Error al eliminar usuario');
        }
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Ocurrió un problema al eliminar el usuario.', variant: 'destructive' });
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchAndSetUsers();
    }
  };

  const FILTER_FIELDS = useMemo(() => (
    [
      usersColumns.find((f) => f.id === 'estado'),
      { id: 'createdAt', label: 'Rango de fechas', type: 'daterange', defaultToday: true },
      { id: 'lavanderia.id', label: 'Centro de Lavado', type: 'select', options: laundryOptionsFilter },
      // Puedes añadir más filtros (rol) si el backend lo soporta
    ].filter(Boolean)
  ), [laundryOptionsFilter]);

  const columns = [
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        { key: 'edit', icon: 'Edit', tooltip: 'Editar' },
        { key: 'change_password', icon: 'KeyRound', tooltip: 'Cambiar Contraseña' },
        { key: 'delete', icon: 'Trash', tooltip: 'Eliminar' },
        {
          key: 'assign_clients',
          icon: 'Truck',
          tooltip: 'Asignar Clientes',
          showWhen: (row) => row.rol === 'conductor',
        },
        {
          key: 'assign_cliente_prendas',
          icon: 'UserCheck',
          tooltip: 'Asignar Clientes',
          showWhen: (row) => row.rol === 'cliente_prendas',
        },
        { key: 'assign_modules', icon: 'ShieldCheck', tooltip: 'Permisos por módulo' },
        { key: 'assign_centros', icon: 'Building2', tooltip: 'Centros asignados' },
      ],
    },
    { id: 'estado', label: 'Estado', type: 'status' },
    { id: 'nombre', label: 'Nombre' },
    { id: 'correo', label: 'Correo' },
    { id: 'rol', label: 'Rol' },
    {
      id: 'lavanderia',
      label: 'Centro de Lavado',
      render: (row) => row?.lavanderia?.nombre || '-',
    },
    {
      id: 'accesos',
      label: 'Accesos',
      render: (row) => (row?.modulosActivos?.length
        ? `Personalizado (${row.modulosActivos.length})`
        : 'Por rol'),
    },
    {
      id: 'centrosAsignados',
      label: 'Centros visibles',
      render: (row) => (row?.centrosAsignados?.length
        ? `${row.centrosAsignados.length} asignado(s)`
        : 'Todos'),
    },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} >
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-4">
            {idLavanderia && (
              <Button asChild variant="outline" size="icon" className="shrink-0">
                <Link to={`/parametrizacion/centros/${idLavanderia}`}>
                  <ArrowLeft className="h-5 w-5" />
                </Link>
              </Button>
            )}
            <div>
              <CardTitle className="text-2xl font-bold">
                Usuarios
                {laundryName && (
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    — {laundryName}
                  </span>
                )}
              </CardTitle>
              <CardDescription>
                {idLavanderia
                  ? 'Gestiona los usuarios del centro de lavado.'
                  : 'Administra la parametrización de usuarios del sistema.'}
              </CardDescription>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Búsqueda rápida (client-side) */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={quickSearch}
                onChange={handleQuickSearchChange}
                placeholder="Búsqueda rápida..."
                className="pl-8 pr-8 h-10 w-56 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                title="Busca por nombre, correo, cédula, rol, estado o centro de lavado"
              />
              {quickSearch && (
                <button
                  type="button"
                  onClick={handleClearQuickSearch}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted flex items-center justify-center"
                  title="Limpiar búsqueda"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <Button variant="outline" onClick={() => setIsDrawerOpen(true)}>
              <FilterIcon className="mr-2 h-4 w-4" /> Filtros
            </Button>
            <Button onClick={() => handleOpenModal('create')}>
              <PlusCircle className="mr-2 h-4 w-4" /> Nuevo Usuario
            </Button>
            <ExportExcelButton
              service={() => exportUsers(filters)}
              columns={columnsExcel}
              fileName={`usuarios_${new Date().toISOString().slice(0, 10)}`}
            />
          </div>
        </CardHeader>
        <CardContent>
          <AppliedFilters
            filters={filters}
            fields={FILTER_FIELDS}
            onRemoveFilter={(key) => setFilters((prev) => {
              const updated = { ...prev };
              delete updated[key];
              return updated;
            })}
          />

          <DataTable
            columns={columns}
            data={dataToShow}
            isLoading={isLoading}
            onAction={(actionType, row) => {
              if (actionType === 'edit') return handleOpenModal('edit', row);
              if (actionType === 'change_password') {
                setUsuarioParaContrasena(row);
                setIsCambiarContrasenaOpen(true);
                return;
              }
              if (actionType === 'delete') return handleDeleteConfirmation(row);
              if (actionType === 'assign_clients') {
                setConductorSeleccionado(row);
                setIsAsignarClientesOpen(true);
              }
              if (actionType === 'assign_cliente_prendas') {
                setClientePrendasUsuario(row);
                setIsAsignarClientePrendasOpen(true);
              }
              if (actionType === 'assign_modules') {
                setUsuarioParaModulos(row);
                setIsAsignarModulosOpen(true);
              }
              if (actionType === 'assign_centros') {
                setUsuarioParaCentros(row);
                setIsAsignarCentrosOpen(true);
              }
            }}
          />

          <div className="mt-4">
            {quickSearch.trim() ? (
              <p className="text-sm text-muted-foreground text-center">
                {dataToShow.length} resultado{dataToShow.length !== 1 ? 's' : ''} para
                <span className="font-medium text-foreground"> &quot;{quickSearch.trim()}&quot;</span>
              </p>
            ) : (
              <Pagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </CardContent>
      </Card>

      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        fields={FILTER_FIELDS}
        onApply={(applied) => { setFilters(applied); setCurrentPage(1); }}
      />

      <DynamicFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveItem}
        fields={usersColumns.map(f => f.id === 'lavanderia' ? { ...f, options: laundryOptions } : f)}
        item={currentItem}
        title={modalMode === 'create' ? 'Crear Usuario' : 'Editar Usuario'}
      />

      <AsignarClientesConductorModal
        isOpen={isAsignarClientesOpen}
        onClose={() => { setIsAsignarClientesOpen(false); setConductorSeleccionado(null); }}
        conductor={conductorSeleccionado}
        onSuccess={fetchAndSetUsers}
      />

      <AsignarModulosUsuarioModal
        isOpen={isAsignarModulosOpen}
        onClose={() => { setIsAsignarModulosOpen(false); setUsuarioParaModulos(null); }}
        usuario={usuarioParaModulos}
        onSuccess={fetchAndSetUsers}
      />

      <AsignarCentrosUsuarioModal
        isOpen={isAsignarCentrosOpen}
        onClose={() => { setIsAsignarCentrosOpen(false); setUsuarioParaCentros(null); }}
        usuario={usuarioParaCentros}
        onSuccess={fetchAndSetUsers}
      />

      <AsignarClientePrendasModal
        isOpen={isAsignarClientePrendasOpen}
        onClose={() => { setIsAsignarClientePrendasOpen(false); setClientePrendasUsuario(null); }}
        usuario={clientePrendasUsuario}
        onSuccess={fetchAndSetUsers}
      />

      <CambiarContrasenaModal
        isOpen={isCambiarContrasenaOpen}
        onClose={() => { setIsCambiarContrasenaOpen(false); setUsuarioParaContrasena(null); }}
        usuario={usuarioParaContrasena}
        onSuccess={fetchAndSetUsers}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará el usuario permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteItem}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

export default UsuariosPage;